import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Per-patient, per-term dismissed-suggestion record for the Narrative
 * NLP feature (PTV2-1051 #5). Once a clinician dismisses a suggested
 * HPO / OMIM term in a given snippet context, the suggestion stops
 * being offered until the snippet's text changes.
 *
 * Keys: `<patientGuid>::<vocabulary>::<termId>::<snippetHash>`. The
 * snippet hash lets two distinct mentions of the same term in
 * different surrounding text remain independently dismissable —
 * matches the spec's "dismissed suggestions tracking" story.
 *
 * Persistence: sessionStorage stub (BE annotation endpoint TBD). Swap
 * for a real `/text-mining/dismissals` write when the BE lands.
 */
interface DismissedState {
  readonly keys: Readonly<Record<string, true>>;
  readonly dismiss: (key: string) => void;
  readonly restore: (key: string) => void;
}

export const useDismissedSuggestionsStore = create<DismissedState>()(
  persist(
    (set) => ({
      keys: {},
      dismiss: (key) => {
        set((state) => {
          if (state.keys[key] === true) return state;
          return { keys: { ...state.keys, [key]: true } };
        });
      },
      restore: (key) => {
        set((state) => {
          if (state.keys[key] !== true) return state;
          const next = { ...state.keys };
          delete next[key];
          return { keys: next };
        });
      },
    }),
    {
      name: 'ptv2.narrativeNlp.dismissed',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ keys: state.keys }),
    },
  ),
);

/** djb2 — small, fast, no crypto requirement. Used to key dismissals. */
export function snippetHash(snippet: string): string {
  let hash = 5381;
  for (let i = 0; i < snippet.length; i += 1) {
    hash = ((hash << 5) + hash + snippet.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36);
}

export function dismissalKey(
  guid: string,
  vocabulary: string,
  termId: string,
  snippet: string,
): string {
  return `${guid}::${vocabulary}::${termId}::${snippetHash(snippet)}`;
}
